import type { QueryClient } from "@tanstack/react-query";
import { apiClient, fetchClient } from "./api-client";
import { useCheckoutStore } from "../stores/checkout-store";
import { useQueryClient } from "@tanstack/react-query";
import { useLayoutEffect, useRef, useState } from "react";

// Hook to create a new checkout (internal use only)
const useCreateCheckout = () => {
  return apiClient.useMutation("post", "/api/public/checkouts");
};

// Helper to check if an error is a checkout ownership error
// Works with both raw errors and processed ApiError objects
export const isCheckoutOwnershipError = (error: unknown): boolean => {
  if (!error) return false;

  // Helper to check if a string contains the ownership error message
  const hasOwnershipMessage = (str: string | undefined | null): boolean => {
    if (!str) return false;
    const lower = str.toLowerCase();
    return (
      lower.includes("you do not own this checkout") ||
      lower.includes("unauthorized - you do not own this checkout") ||
      lower.includes("do not own this checkout") ||
      (lower.includes("checkout") && lower.includes("own"))
    );
  };

  // openapi-fetch / openapi-react-query throw the parsed JSON body on error — for
  // RFC 7807 responses that is a Problem Detail with `detail`, not `message`.
  // Also matches processed ApiError objects from handleApiError.
  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error
  ) {
    const apiError = error as {
      status?: number;
      message?: string;
      error?: string;
      detail?: string;
      title?: string;
    };
    if (apiError.status === 403) {
      if (
        hasOwnershipMessage(apiError.message) ||
        hasOwnershipMessage(apiError.error) ||
        hasOwnershipMessage(apiError.detail) ||
        hasOwnershipMessage(apiError.title)
      ) {
        return true;
      }
    }
  }

  // Check if it's a raw API error response (from openapi-react-query)
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error
  ) {
    const apiError = error as {
      response?: {
        status?: number;
        data?: { error?: string; detail?: string; title?: string };
        statusText?: string;
      };
    };
    const status = apiError.response?.status;
    const data = apiError.response?.data;

    if (status === 403 && data && typeof data === "object") {
      const errorMessage =
        (typeof data.error === "string" ? data.error : undefined) ??
        (typeof data.detail === "string" ? data.detail : undefined) ??
        (typeof data.title === "string" ? data.title : undefined);
      const statusText = apiError.response?.statusText;
      if (
        hasOwnershipMessage(errorMessage) ||
        hasOwnershipMessage(statusText)
      ) {
        return true;
      }
    }
  }

  // Check error.message directly (some error formats)
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    if (hasOwnershipMessage((error as { message: string }).message)) {
      return true;
    }
  }

  // Check error.error (some error formats)
  if (
    typeof error === "object" &&
    error !== null &&
    "error" in error
  ) {
    const err = (error as { error?: unknown }).error;
    if (typeof err === "string" && hasOwnershipMessage(err)) {
      return true;
    }
    if (
      typeof err === "object" &&
      err !== null &&
      "message" in err &&
      typeof (err as { message?: unknown }).message === "string"
    ) {
      if (hasOwnershipMessage((err as { message: string }).message)) {
        return true;
      }
    }
  }

  return false;
};

/** API problem detail when a checkout id does not exist (RFC 7807). */
const CHECKOUT_NOT_FOUND_DETAIL = "Checkout not found";

/**
 * True when the API reports the checkout row is missing (404 + problem detail).
 * Used for mutation errors so we do not confuse with e.g. "Event not found" on add-item.
 */
export const isCheckoutNotFoundError = (error: unknown): boolean => {
  if (!error) return false;

  const detailMatches = (str: string | undefined | null): boolean =>
    str === CHECKOUT_NOT_FOUND_DETAIL;

  if (typeof error === "object" && error !== null && "status" in error) {
    const apiError = error as {
      status?: number;
      detail?: string;
      message?: string;
    };
    if (apiError.status === 404) {
      if (detailMatches(apiError.detail) || detailMatches(apiError.message)) {
        return true;
      }
    }
  }

  if (typeof error === "object" && error !== null && "response" in error) {
    const apiError = error as {
      response?: {
        status?: number;
        data?: { detail?: string; error?: string; title?: string };
      };
    };
    const status = apiError.response?.status;
    const data = apiError.response?.data;
    if (status === 404 && data && typeof data === "object") {
      const detail =
        (typeof data.detail === "string" ? data.detail : undefined) ??
        (typeof data.error === "string" ? data.error : undefined);
      if (detailMatches(detail)) {
        return true;
      }
    }
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    if (detailMatches((error as { message: string }).message)) {
      return true;
    }
  }

  if (typeof error === "object" && error !== null && "error" in error) {
    const err = (error as { error?: unknown }).error;
    if (typeof err === "string" && detailMatches(err)) {
      return true;
    }
  }

  return false;
};

/** Ownership conflict or checkout id no longer exists — safe to rotate session. */
export const isRecoverableCheckoutSessionError = (error: unknown): boolean =>
  isCheckoutOwnershipError(error) || isCheckoutNotFoundError(error);

function invalidateCheckoutDetailQueries(queryClient: QueryClient): void {
  queryClient.invalidateQueries({
    queryKey: ["get", "/api/public/checkouts/{checkoutId}"],
  });
}

/**
 * Clears the stored checkout id, creates a new checkout via the API, and
 * persists the new id. Optionally invalidates checkout detail queries.
 */
export async function createNewCheckoutAndSyncStore(
  queryClient?: QueryClient,
): Promise<string | null> {
  useCheckoutStore.getState().clearCheckout();
  const { data, error } = await fetchClient.POST("/api/public/checkouts", {});
  if (error || !data?.checkout?.id) {
    return null;
  }
  useCheckoutStore.getState().setCheckoutId(data.checkout.id);
  if (queryClient) {
    invalidateCheckoutDetailQueries(queryClient);
  }
  return data.checkout.id;
}

// Hook to fetch checkout data
// Automatically creates a new checkout if the current one is missing or no longer owned
export const useCheckout = (checkoutId: string | null) => {
  const queryClient = useQueryClient();
  const isRecreatingRef = useRef(false);
  const [isRecreating, setIsRecreating] = useState(false);
  const handledErrorRef = useRef<unknown>(null);
  const shouldSuppressErrorRef = useRef(false);

  const query = apiClient.useQuery(
    "get",
    "/api/public/checkouts/{checkoutId}",
    {
      params: { path: { checkoutId: checkoutId! } },
    },
    {
      enabled: !!checkoutId && !isRecreatingRef.current && !isRecreating,
      retry: false, // Don't retry on error, we'll handle it manually
    },
  );

  // Check for ownership errors synchronously
  const isOwnershipError = query.error && isCheckoutOwnershipError(query.error);
  // Also check if it's any 403 error on checkout endpoint (defensive check)
  const is403OnCheckout = query.error &&
    typeof query.error === "object" &&
    query.error !== null &&
    "response" in query.error &&
    (query.error as { response?: { status?: number } }).response?.status === 403;
  // GET checkout only returns 404 when the checkout row is missing
  const is404OnCheckout = query.error &&
    typeof query.error === "object" &&
    query.error !== null &&
    "response" in query.error &&
    (query.error as { response?: { status?: number } }).response?.status === 404;
  const is404CheckoutNotFound =
    !!query.error && (is404OnCheckout || isCheckoutNotFoundError(query.error));

  const isRecoverableQueryError =
    (isOwnershipError || is403OnCheckout || is404CheckoutNotFound) &&
    handledErrorRef.current !== query.error;

  // Mark error for suppression immediately (synchronously, before render)
  if (isOwnershipError || is403OnCheckout || is404CheckoutNotFound) {
    shouldSuppressErrorRef.current = true;
  } else if (!query.error) {
    shouldSuppressErrorRef.current = false;
  }

  // Handle missing / invalid checkout session immediately when detected
  useLayoutEffect(() => {
    if (!checkoutId || isRecreatingRef.current || isRecreating || !isRecoverableQueryError) return;

    // Mark this error as handled
    handledErrorRef.current = query.error;

    isRecreatingRef.current = true;
    setIsRecreating(true);

    void createNewCheckoutAndSyncStore(queryClient)
      .then((newId) => {
        if (newId) {
          handledErrorRef.current = null;
        }
      })
      .catch(() => {
        handledErrorRef.current = null;
      })
      .finally(() => {
        isRecreatingRef.current = false;
        setIsRecreating(false);
      });
  }, [
    isRecoverableQueryError,
    checkoutId,
    query.error,
    queryClient,
    isRecreating,
  ]);

  // Suppress errors we rotate the session for (403 ownership, 403 defensive, 404 gone)
  const shouldSuppress =
    shouldSuppressErrorRef.current ||
    isRecreating ||
    isOwnershipError ||
    is403OnCheckout ||
    is404CheckoutNotFound;

  return {
    ...query,
    isLoading: query.isLoading || isRecreating,
    error: shouldSuppress ? null : query.error,
  };
};

// Hook to fetch checkout participants and consent status
export const useCheckoutParticipants = (checkoutId: string | null) => {
  return apiClient.useQuery(
    "get",
    "/api/public/checkouts/{checkoutId}/participants",
    {
      params: { path: { checkoutId: checkoutId! } },
    },
    {
      enabled: !!checkoutId,
    },
  );
};

// Hook to patch participant consent across all checkout items
export const usePatchParticipantConsent = () => {
  return apiClient.useMutation(
    "patch",
    "/api/public/checkouts/{checkoutId}/participants/consent",
  );
};

// Hook to add item to checkout
export const useAddCheckoutItem = () => {
  return apiClient.useMutation(
    "post",
    "/api/public/checkouts/{checkoutId}/items",
  );
};

// Hook to remove checkout item
export const useRemoveCheckoutItem = () => {
  return apiClient.useMutation(
    "delete",
    "/api/public/checkouts/{checkoutId}/items/{itemId}",
  );
};

// Hook to create Stripe checkout session
export const useCreateStripeSession = () => {
  return apiClient.useMutation(
    "post",
    "/api/public/checkouts/{checkoutId}/stripe/session",
  );
};

// Hook to complete checkout (submit and return success data)
// Note: API types may need regeneration to include this path
export const useCompleteCheckout = () => {
  return apiClient.useMutation(
    "post",
    "/api/public/checkouts/{checkoutId}/complete",
  );
};

// Utility function to ensure checkout exists
export const useEnsureCheckout = () => {
  const { checkoutId, setCheckoutId } = useCheckoutStore();
  const createCheckout = useCreateCheckout();

  const ensureCheckout = async () => {
    if (checkoutId) {
      return checkoutId;
    }

    const result = await createCheckout.mutateAsync({});
    if (!result) {
      throw new Error("Failed to create checkout");
    }
    const newCheckoutId = result.checkout.id;
    setCheckoutId(newCheckoutId);
    return newCheckoutId;
  };

  return { ensureCheckout, isLoading: createCheckout.isPending };
};
